import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Facebook, Instagram, RefreshCw, Loader2, ExternalLink,
  Heart, MessageCircle, Share2, Eye,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

export default function LiveSocialMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/social");
      setData(r.data);
    } finally { setLoading(false); }
  };

  const hydrate = async () => {
    setHydrating(true);
    try {
      const r = await api.post("/analytics/social/hydrate?days=30");
      toast.success(`Refreshed ${r.data.hydrated} source(s) from Meta Graph API`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hydrate failed");
    } finally { setHydrating(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="h-64 glass rounded-2xl animate-pulse" />;

  if (!data?.has_meta_connection) {
    return (
      <div className="glass rounded-2xl p-8 text-center" data-testid="social-no-connection">
        <div className="text-white/60 text-sm mb-4">
          Connect Facebook + Instagram via <a className="text-indigo-300 underline" href="/dashboard/connect">Settings → Connections</a> to see live audience numbers from the Meta Graph API.
        </div>
      </div>
    );
  }

  const a = data.aggregate || {};
  const snapshots = data.snapshots || [];

  return (
    <div className="space-y-6" data-testid="live-social-metrics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-black flex items-center gap-2">
            Live social metrics
            <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full">LIVE · Meta Graph API</span>
          </h3>
          <p className="text-xs text-white/40 mt-1">
            {data.fetched_at ? `Cached ${new Date(data.fetched_at).toLocaleString()}` : "No cached snapshot yet — hit refresh."}
          </p>
        </div>
        <button onClick={hydrate} disabled={hydrating} data-testid="social-hydrate-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {hydrating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh from Meta
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiTile icon={Facebook} label="FB impressions (30d)" value={fmt(a.fb_impressions)} color="blue" />
        <KpiTile icon={Facebook} label="FB engagements (30d)" value={fmt(a.fb_engagements)} color="blue" />
        <KpiTile icon={Facebook} label="FB page fans" value={fmt(a.fb_fans)} color="blue" />
        <KpiTile icon={Instagram} label="IG impressions (30d)" value={fmt(a.ig_impressions)} color="pink" />
        <KpiTile icon={Instagram} label="IG reach (30d)" value={fmt(a.ig_reach)} color="pink" />
        <KpiTile icon={Instagram} label="IG followers" value={fmt(a.ig_followers)} color="pink" />
      </div>

      {snapshots.map((s) => (
        <div key={`${s.platform}-${s.page_id || s.ig_user_id}`}
          className="glass rounded-2xl p-5" data-testid={`social-snapshot-${s.platform}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {s.platform === "facebook" ? <Facebook className="h-5 w-5 text-blue-400" /> : <Instagram className="h-5 w-5 text-pink-400" />}
              <span className="font-semibold">{(s.data || {}).page_name || s.platform}</span>
            </div>
            <span className="text-xs text-white/40 font-mono">Period: {s.period_days || 30}d</span>
          </div>
          {(s.data || {}).error && (
            <div className="text-xs text-rose-300 mb-2">⚠ {s.data.error}</div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
            {Object.entries((s.data || {}).metrics || {}).map(([k, v]) => (
              <div key={k} className="glass rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{k.replace(/_/g, " ")}</div>
                <div className="font-heading text-lg font-black mt-1">{fmt(v)}</div>
              </div>
            ))}
          </div>
          {(s.data?.posts || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Top recent posts</div>
              <div className="space-y-2">
                {s.data.posts.slice(0, 5).map((p) => (
                  <div key={p.id} className="glass rounded-lg p-3 text-xs">
                    <div className="text-white/70 line-clamp-2 mb-1">{p.message || "(no caption)"}</div>
                    <div className="flex items-center gap-3 text-white/40 font-mono">
                      <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3" />{p.reactions}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />{p.comments}</span>
                      {p.shares !== undefined && <span className="inline-flex items-center gap-1"><Share2 className="h-3 w-3" />{p.shares}</span>}
                      {p.permalink && (
                        <a href={p.permalink} target="_blank" rel="noreferrer" className="text-indigo-300 hover:underline inline-flex items-center gap-1 ml-auto">
                          <ExternalLink className="h-3 w-3" /> view
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, color }) {
  const colorMap = { blue: "text-blue-400", pink: "text-pink-400" };
  return (
    <div className="glass rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</span>
        <Icon className={`h-4 w-4 ${colorMap[color] || ""}`} />
      </div>
      <div className="font-heading text-2xl font-black truncate">{value}</div>
    </div>
  );
}
